import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createTrimesterEvaluation } from "../services/evaluation";
import { listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { createSubject } from "../services/subject";
import WizardSteps from "./shared/WizardSteps";
import { levelKey } from "../utils/classesLabels";
import { isCambridgeSucursal } from "../services/auth";
import { CAMBRIDGE_CATEGORIES, COMPORTAMENTO_SUBJECT_NAME } from "../utils/cambridgeGrading";

const TOTAL_STEPS = 3;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
// Values match sge_infantil_web's `shifts` (src/pages/grade/list/index.js)
// and every gradebook/evaluation record migrated from it — real data uses
// these full labels as the `period` value, not "1"/"2"/"3".
const PERIOD_OPTIONS = [
  { value: "Primeiro Trimestre", label: "1º Trimestre" },
  { value: "Segundo Trimestre", label: "2º Trimestre" },
  { value: "Terceiro Trimestre", label: "3º Trimestre" },
];

// Split out of the former AvaliacoesLayer (which mixed the wizard with the
// evaluation table). Mirrors CadastrarProdutoLayer: this screen is hidden
// from the menu and only reachable through the "Adicionar Avaliação" button
// on ListaAvaliacoesLayer — see App.js route
// `/academico/avaliacoes/cadastrar`. The level/subject/year/period the user
// had selected on the list, if any, arrives via navigation state and
// pre-fills step 1 here.
const CadastrarAvaliacaoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const carried = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const [frequencies, setFrequencies] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [levelId, setLevelId] = useState(carried.levelId || "");
  const [subjectId, setSubjectId] = useState(carried.subjectId || "");
  const [year, setYear] = useState(carried.year || currentYear);
  const [period, setPeriod] = useState(carried.period || "Primeiro Trimestre");
  const [evaluationName, setEvaluationName] = useState("");
  const [weight, setWeight] = useState("");
  const [evaluationType, setEvaluationType] = useState("ACS");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [creatingComportamento, setCreatingComportamento] = useState(false);
  const [done, setDone] = useState(false);

  const isCambridge = isCambridgeSucursal();
  const hasComportamentoSubject = subjects.some(
    (s) => (s.subjectName || "").trim().toLowerCase() === COMPORTAMENTO_SUBJECT_NAME.toLowerCase(),
  );

  const stepLabels = [
    t(levelKey("avaliacao.wizard.step.context")),
    t("avaliacao.wizard.step.data"),
    t("avaliacao.wizard.step.confirm"),
  ];

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    if (!levelId) {
      setSubjects([]);
      return;
    }
    listSubjectsByLevel(levelId).then((resp) => setSubjects(resp.data || [])).catch(() => setSubjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId]);

  const handleLevelChange = (e) => {
    setLevelId(e.target.value);
    setSubjectId("");
  };

  const levelName = frequencies.find((f) => f.id === levelId)?.description || "-";
  const subjectName = subjects.find((s) => s.id === subjectId)?.subjectName || "-";
  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || "-";

  const nextStep = () => {
    if (currentStep === 1 && (!levelId || !subjectId)) {
      toast.error(t(levelKey("avaliacao.error.requiredContext")));
      return;
    }
    if (currentStep === 2 && !evaluationName) {
      toast.error(t("avaliacao.error.requiredName"));
      return;
    }
    if (currentStep === 2 && isCambridge && !category) {
      toast.error(t("avaliacao.error.requiredCategory"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  // Keeps the level/subject/year/period context — evaluations are usually
  // added in batches for the same subject+period — and jumps straight back
  // to the data step instead of making the user reselect the context.
  const restart = () => {
    setEvaluationName("");
    setWeight("");
    setEvaluationType("ACS");
    setCategory("");
    setCurrentStep(2);
    setDone(false);
  };

  // Behaviour (Comportamento) não é uma disciplina académica normal — é a
  // disciplina "guarda-chuva" onde o backend (utils/reportCard.js) procura a
  // nota de comportamento do boletim. Em vez de obrigar o admin a ir criá-la
  // à mão em Disciplinas antes de poder lançar a avaliação de Comportamento,
  // criamo-la aqui mesmo, só na primeira vez que é precisa para este nível.
  const handleCreateComportamento = async () => {
    setCreatingComportamento(true);
    try {
      const resp = await createSubject({ subjectName: COMPORTAMENTO_SUBJECT_NAME, levelId, isCurricular: false });
      const newSubjects = await listSubjectsByLevel(levelId);
      setSubjects(newSubjects.data || []);
      const created = resp?.data?.data;
      if (created?.id) setSubjectId(created.id);
    } catch (err) {
      toast.error(t("avaliacao.error.createComportamento"));
    } finally {
      setCreatingComportamento(false);
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategory(value);
    const def = CAMBRIDGE_CATEGORIES.find((c) => c.value === value);
    if (def) {
      setEvaluationName(def.label);
      setWeight(String(def.defaultWeight));
    }
  };

  const handleSubmit = async () => {
    const subject = subjects.find((s) => s.id === subjectId);
    setSubmitting(true);
    try {
      await createTrimesterEvaluation({
        levelId,
        subject: { id: subject.id, name: subject.subjectName },
        evaluationName,
        weight: weight ? Number(weight) : undefined,
        period,
        year,
        evaluationType,
        category: isCambridge ? category : undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("avaliacao.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  const backToList = () => {
    navigate("/academico/avaliacoes", { state: { levelId, subjectId, year, period } });
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("avaliacao.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("avaliacao.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t(levelKey("turma.field.educationLevel"))}</label>
                      <select className='form-select' value={levelId} onChange={handleLevelChange} required>
                        <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("disciplina.field.subjectName")}</label>
                      <select
                        className='form-select'
                        value={subjectId}
                        onChange={(e) => setSubjectId(e.target.value)}
                        disabled={!levelId}
                        required
                      >
                        <option value=''>{t("avaliacao.field.subject.placeholder")}</option>
                        {subjects.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.subjectName}
                          </option>
                        ))}
                      </select>
                      {isCambridge && levelId && !hasComportamentoSubject && (
                        <button
                          type='button'
                          className='btn btn-link btn-sm px-0 mt-4'
                          onClick={handleCreateComportamento}
                          disabled={creatingComportamento}
                        >
                          <Icon icon='ic:baseline-plus' />
                          {creatingComportamento ? t("global.loading") : t("avaliacao.action.createComportamento")}
                        </button>
                      )}
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("turma.field.academicYear")}</label>
                      <select className='form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {YEAR_OPTIONS.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("avaliacao.field.period")}</label>
                      <select className='form-select' value={period} onChange={(e) => setPeriod(e.target.value)}>
                        {PERIOD_OPTIONS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-5'>
                      <label className='form-label'>{t("avaliacao.field.name")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="f7:person" />
                        </span>
                        <input type='text' className='form-control' value={evaluationName} onChange={(e) => setEvaluationName(e.target.value)} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("avaliacao.field.weight")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:pen-2-outline" />
                        </span>
                        <input type='number' min='0' max='100' className='form-control' value={weight} onChange={(e) => setWeight(e.target.value)} />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      {isCambridge ? (
                        <>
                          <label className='form-label'>{t("avaliacao.field.category")}</label>
                          <select className='form-select' value={category} onChange={handleCategoryChange} required>
                            <option value=''>{t("avaliacao.field.category.placeholder")}</option>
                            {CAMBRIDGE_CATEGORIES.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <label className='form-label'>{t("avaliacao.field.type")}</label>
                          <div className="icon-field">
                            <span className="icon">
                              <Icon icon="solar:pen-2-outline" />
                            </span>
                            <input type='text' className='form-control' value={evaluationType} onChange={(e) => setEvaluationType(e.target.value)} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32' onClick={prevStep}>
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 3 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("avaliacao.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t(levelKey("turma.field.educationLevel"))}:</strong> {levelName}
                    </li>
                    <li>
                      <strong>{t("disciplina.field.subjectName")}:</strong> {subjectName}
                    </li>
                    <li>
                      <strong>{t("turma.field.academicYear")}:</strong> {year}
                    </li>
                    <li>
                      <strong>{t("avaliacao.field.period")}:</strong> {periodLabel}
                    </li>
                    <li>
                      <strong>{t("avaliacao.field.name")}:</strong> {evaluationName}
                    </li>
                    <li>
                      <strong>{t("avaliacao.field.weight")}:</strong> {weight || 0}%
                    </li>
                    <li>
                      <strong>{isCambridge ? t("avaliacao.field.category") : t("avaliacao.field.type")}:</strong>{" "}
                      {isCambridge ? CAMBRIDGE_CATEGORIES.find((c) => c.value === category)?.label || "-" : evaluationType}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("avaliacao.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("avaliacao.action.createAnother")}
                    </button>
                    <button type='button' className='btn btn-primary-600 px-32' onClick={backToList}>
                      <i className='ri-arrow-left-line' /> {t("avaliacao.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarAvaliacaoLayer;
